import { Drawer, SearchInput } from '@economic/taco';
import { Trans, t } from '@lingui/macro';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Authorise } from '@economic/react-app';
import { ApplaiView } from '~/app/shared/components/Help/ai-chat/ApplaiView';
import { DrilldownButton } from '~/app/shared/components/Help/overview/DrilldownButton';
import { useHelpDrawer } from '~/app/shared/components/Help/HelpDrawerContext';
import { LinkButton } from '~/app/shared/components/Help/overview/LinkButton';
import IconAiStars from '~/app/shared/components/Help/testIcons/IconAiStars';
import Intercom from '~/app/shared/components/Intercom';
import { MailView } from '~/app/shared/components/Help/mail/MailView';
import { HelpDrawerActions } from '~/app/shared/components/Help/HelpDrawerActions';
import { PhoneView } from '~/app/shared/components/Help/phone/PhoneView';

export function HelpOverview(): React.JSX.Element {
    const chatWithOurAiChatbot = t`AI Chat`;
    const { navigate } = useHelpDrawer();
    const { pathname } = useLocation();

    // Hardcoded path for user testing purposes to only show to search variant for AI chat.
    const aiChatSearch = pathname.startsWith('/sales');

    return (
        <>
            <Drawer.Title>
                {t`Help`}
                <HelpDrawerActions />
            </Drawer.Title>
            <div className="p-4 overflow-auto flex-1">
                {aiChatSearch ? (
                    <div>
                        <SearchInput
                            placeholder={t`Ask a question about accounting in e-conomic…`}
                            className="w-full h-10"
                            autoFocus
                            onSearch={question =>
                                navigate(<ApplaiView title={chatWithOurAiChatbot} initialQuestion={question?.toString()} />)
                            }
                        />
                        <p className="mt-2 mb-4 inline-flex items-center text-xs text-grey-700">
                            <span className="text-xs">
                                <IconAiStars />
                            </span>
                            {t`Search powered by Chat GPT.`}&nbsp;
                            <a href="https://www.e-conomic.dk/sikkerhed/privacy" target="_blank">{t`Learn more`}</a>
                        </p>
                    </div>
                ) : null}
                <ul className="flex flex-col gap-2">
                    {!aiChatSearch ? (
                        <li>
                            <DrilldownButton
                                testIcon="ai-chat-solid"
                                description={
                                    <Trans>
                                        Expected response time: <b>0min</b>
                                    </Trans>
                                }
                                header={chatWithOurAiChatbot}
                                onClick={() => navigate(<ApplaiView title={chatWithOurAiChatbot} />)}
                                showNextIcon
                            />
                        </li>
                    ) : null}
                    <Authorise path="chat">
                        <li>
                            <Intercom>
                                {({ toggle }) => (
                                    <DrilldownButton
                                        header={aiChatSearch ? t`Chat` : t`Support Chat`}
                                        description={
                                            <Trans>
                                                Expected response time: <b>15min</b>
                                            </Trans>
                                        }
                                        icon="chat-solid"
                                        onClick={() => toggle()}
                                        showNextIcon
                                    />
                                )}
                            </Intercom>
                        </li>
                    </Authorise>
                    <li>
                        <DrilldownButton
                            header={<Trans id="HELP_PHONE">Phone</Trans>}
                            description={
                                <Trans>
                                    Expected response time: <b>40min</b>
                                </Trans>
                            }
                            testIcon="phone-solid"
                            onClick={() => navigate(<PhoneView />)}
                            showNextIcon
                        />
                    </li>
                    <li>
                        <DrilldownButton
                            header={t`Mail`}
                            description={
                                <Trans>
                                    Expected response time: <b>Within 48 hours</b>
                                </Trans>
                            }
                            testIcon="envelope-solid"
                            onClick={() => navigate(<MailView />)}
                            showNextIcon
                        />
                    </li>
                    <li>
                        <DrilldownButton
                            header={t`e-copedia`}
                            description={t`Find help articles for e-conomic`}
                            icon="e-copedia"
                            onClick={() => window.open('https://www.e-conomic.dk/support/e-copedia', '_blank')}
                        />
                    </li>
                </ul>
                <ul className="flex flex-col">
                    <li>
                        <LinkButton
                            icon="play"
                            title={t`Help videos`}
                            link="https://www.e-conomic.dk/support/e-copedia/hjaelpevideoer"
                            mode="external"
                        />
                    </li>
                    <li>
                        <LinkButton
                            icon="courses"
                            title={t`Online courses`}
                            link="https://www.e-conomic.dk/support/online-live-kursus"
                            mode="external"
                        />
                    </li>
                    <li>
                        <LinkButton
                            icon="book"
                            title={t`Accounting dictionary`}
                            link="https://www.e-conomic.dk/regnskabsprogram/ordbog"
                            mode="external"
                        />
                    </li>
                    <li>
                        <LinkButton
                            icon="star"
                            title={t`Latest updates in e-conomic`}
                            link="https://www.e-conomic.dk/support/forbedringer"
                            mode="embedded"
                        />
                    </li>
                    <li>
                        <LinkButton
                            icon="person-change"
                            title={t`Support options`}
                            link="https://www.e-conomic.dk/support"
                            mode="embedded"
                        />
                    </li>
                    <li>
                        <LinkButton
                            icon="document"
                            title={t`Privacy policy`}
                            link="https://www.e-conomic.dk/sikkerhed/privacy"
                            mode="embedded"
                        />
                    </li>
                </ul>
            </div>
        </>
    );
}
